import { Portal } from '../portal'
import { cn } from '@/lib/utils'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  /** Optional secondary line — useful for explaining a destructive action. */
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'default'
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
}: ConfirmDialogProps) {
  if (!isOpen) return null

  return (
    <Portal>
      {/* Blurred backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Dialog card. Layout shifts to flex-col when a description is
          present so the explanatory text wraps cleanly on its own line. */}
      <div className="fixed inset-x-0 top-0 z-50 flex justify-center pointer-events-none">
        <div
          className={cn(
            'mt-20 mx-4 px-6 py-4 rounded-2xl bg-black/90 backdrop-blur-xl border border-white/20 shadow-2xl pointer-events-auto animate-slide-down',
            description
              ? 'flex flex-col gap-4 max-w-sm'
              : 'flex items-center gap-6',
          )}
        >
          <div className={cn(description && 'space-y-1')}>
            <p className="text-sm text-white font-medium">{title}</p>
            {description && (
              <p className="text-xs text-white/60 leading-relaxed">
                {description}
              </p>
            )}
          </div>
          <div className={cn('flex gap-2', description && 'justify-end')}>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/[0.05]"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                variant === 'danger'
                  ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300"
                  : "bg-white/10 text-white hover:bg-white/20"
              )}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
